if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        // Fall back to cart-notification, then redirect to cart page
        const cartDrawerEl =
          document.querySelector("cart-drawer") ||
          document.getElementById("CartDrawer");
        const cartNotificationEl = document.querySelector("cart-notification");
        // If the drawer exists in the DOM (meaning the section was added), prefer it. Otherwise use notification.
        this.cart = cartDrawerEl || cartNotificationEl || null;

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (
          this.cart &&
          (this.cart.tagName === "CART-DRAWER" || this.cart.id === "CartDrawer")
        ) {
          this.submitButton.setAttribute("aria-haspopup", "dialog");
        }

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      isPartialAddResponse(response) {
        if (!response?.status) return false;

        const hasItems =
          Array.isArray(response.items) && response.items.length > 0;
        const hasSections = Boolean(
          response.sections && Object.keys(response.sections).length,
        );
        const hasAvailabilityMessage =
          /added to your cart due to availability/i.test(
            response.description || "",
          );

        return hasItems || hasSections || hasAvailabilityMessage;
      }

      async renderPartialAddResponse(response) {
        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        if (
          response.sections &&
          Object.keys(response.sections).length &&
          typeof this.cart.renderContents === "function"
        ) {
          this.cart.renderContents(response);
          return;
        }

        if (typeof this.cart.fetchLatestSections === "function") {
          await this.cart.fetchLatestSections();
          if (typeof this.cart.open === "function") this.cart.open();
          return;
        }

        window.location = window.routes.cart_url;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        // Ensure the form submits the currently selected variant id from
        // variant selectors, even if async fragment updates are still in flight.
        const productInfo = this.closest("product-info");
        if (
          productInfo?.getAllVariants &&
          productInfo?.getSelectedOptionsByPosition &&
          productInfo?.findVariantBySelectedOptions
        ) {
          try {
            const selectedByPosition = productInfo.getSelectedOptionsByPosition(
              productInfo.variantSelectors,
            );
            const liveVariant = productInfo.findVariantBySelectedOptions(
              productInfo.getAllVariants(),
              selectedByPosition,
            );
            if (liveVariant?.id) {
              this.variantIdInput.value = liveVariant.id;
            }
          } catch (e) {
            /* best effort */
          }
        }

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);

        // Submit the visible quantity input value explicitly.
        // This avoids stale/default quantity values when variant updates race.
        if (productInfo) {
          const quantityInput =
            productInfo.querySelector(
              `.quantity__input[form="${this.form.id}"]`,
            ) || productInfo.querySelector(".quantity__input");
          if (quantityInput?.value) {
            formData.set("quantity", quantityInput.value);
          }
        }

        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            const isPartialAdd = this.isPartialAddResponse(response);

            if (response.status && !isPartialAdd) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (response.status && isPartialAdd) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    if (isPartialAdd) {
                      this.renderPartialAddResponse(response);
                    } else {
                      this.cart.renderContents(response);
                    }
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              if (isPartialAdd) {
                await this.renderPartialAddResponse(response);
              } else {
                this.cart.renderContents(response);
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("aria-disabled", "true");
          this.submitButton.classList.add("disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("aria-disabled");
          this.submitButton.classList.remove("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    },
  );
}
